import type { EnvironmentId } from "@cmmd-forge/contracts";
import { getKnownEnvironmentWsBaseUrl } from "@cmmd-forge/client-runtime";

import { resolveRemoteWebSocketConnectionUrl } from "../remote/api";
import { resolvePrimaryWebSocketConnectionUrl } from "../primary";
import { readEnvironmentBootstrapEnvelope } from "../bootstrapEnvelope";
import { hydrateRuntimeBridgeBootstrapEnvelope } from "../runtimeBridge";
import { WsTransport } from "../../rpc/wsTransport";
import { createWsRpcClient, type WsRpcClient } from "../../rpc/wsRpcClient";
import { isCloudBearerAuthFailure } from "../../lib/cloudAuthFailure";
import { setCloudAuthFailure } from "../../rpc/wsConnectionState";

export type DesktopCloudAuthTarget = {
  readonly bearerToken: string;
  readonly httpBaseUrl: string;
  readonly wsBaseUrl: string;
};

export type DesktopLocalAuthTarget = DesktopCloudAuthTarget & {
  readonly label: string;
};

export function readDesktopCloudAuthTarget(): DesktopCloudAuthTarget | undefined {
  if (typeof window === "undefined") return undefined;
  const bootstrap = readEnvironmentBootstrapEnvelope();
  if (bootstrap?.primaryMode !== "cloud" || !bootstrap.bearerToken) return undefined;
  return {
    bearerToken: bootstrap.bearerToken,
    httpBaseUrl: bootstrap.httpBaseUrl,
    wsBaseUrl: bootstrap.wsBaseUrl,
  };
}

export function readDesktopLocalAuthTarget(): DesktopLocalAuthTarget | undefined {
  if (typeof window === "undefined") return undefined;
  const bootstrap = readEnvironmentBootstrapEnvelope();
  const localBootstrap = bootstrap?.primaryMode === "cloud" ? bootstrap.desktopLocal : bootstrap;
  if (!localBootstrap?.httpBaseUrl || !localBootstrap.wsBaseUrl || !localBootstrap.bearerToken) {
    return undefined;
  }
  return {
    bearerToken: localBootstrap.bearerToken,
    httpBaseUrl: localBootstrap.httpBaseUrl,
    wsBaseUrl: localBootstrap.wsBaseUrl,
    label: localBootstrap.label,
  };
}

export function createPrimaryEnvironmentClient(
  knownEnvironment: Parameters<typeof getKnownEnvironmentWsBaseUrl>[0],
): WsRpcClient {
  const wsBaseUrl = getKnownEnvironmentWsBaseUrl(knownEnvironment);
  if (!wsBaseUrl) {
    throw new Error(
      `Unable to resolve websocket URL for ${knownEnvironment?.label ?? "primary environment"}.`,
    );
  }

  return createWsRpcClient(
    new WsTransport(async () => {
      await hydrateRuntimeBridgeBootstrapEnvelope().catch(() => null);
      const cloudAuthTarget = readDesktopCloudAuthTarget();
      if (cloudAuthTarget) {
        try {
          return await resolveRemoteWebSocketConnectionUrl(cloudAuthTarget);
        } catch (error) {
          if (isCloudBearerAuthFailure(error)) setCloudAuthFailure("ws-token");
          throw error;
        }
      }
      return resolvePrimaryWebSocketConnectionUrl(wsBaseUrl);
    }),
  );
}

export function createDesktopLocalEnvironmentClient(input: {
  readonly environmentId: EnvironmentId;
  readonly httpBaseUrl: string;
  readonly wsBaseUrl: string;
  readonly bearerToken: string;
  readonly onConnecting: (environmentId: EnvironmentId) => void;
  readonly onConnected: (environmentId: EnvironmentId) => void;
  readonly onError: (environmentId: EnvironmentId, message: string) => void;
  readonly onDisconnected: (environmentId: EnvironmentId, reason: string) => void;
}): WsRpcClient {
  return createWsRpcClient(
    new WsTransport(() => resolveRemoteWebSocketConnectionUrl(input), {
      trackGlobalConnection: false,
      onAttempt: () => input.onConnecting(input.environmentId),
      onOpen: () => input.onConnected(input.environmentId),
      onError: (message: string) => input.onError(input.environmentId, message),
      onClose: (details: { readonly reason: string }) =>
        input.onDisconnected(input.environmentId, details.reason),
    }),
  );
}
